import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { LOGO_URL } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShieldCheck, CalendarRange, Images } from "lucide-react";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (tab === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bw-gradient text-white relative overflow-hidden">
        <div className="flex items-center gap-3 relative z-10">
          <img src={LOGO_URL} alt="BEAUTWELLZ" className="h-11 w-11 rounded-xl object-cover ring-2 ring-white/40" />
          <div>
            <p className="font-display font-extrabold text-xl">BEAUTWELLZ</p>
            <p className="text-xs text-white/70 tracking-widest">MAKE YOUR BRAND</p>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <h2 className="font-display text-4xl font-extrabold leading-tight">One dashboard for your whole brand.</h2>
          <p className="mt-4 text-white/80">Manage your team, customers, social planning and media — all in one beautiful workspace.</p>
          <div className="mt-8 space-y-3">
            {[
              [ShieldCheck, "Role-based staff access"],
              [CalendarRange, "Plan posts across 8 platforms"],
              [Images, "Organize reels, pics & videos"],
            ].map(([Icon, t]) => (
              <div key={t} className="flex items-center gap-3 text-sm text-white/90">
                <span className="h-9 w-9 rounded-lg bg-white/15 flex items-center justify-center"><Icon className="h-4 w-4" /></span>
                {t}
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-white/50 relative z-10">© {new Date().getFullYear()} BEAUTWELLZ</p>
        <div className="absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-pink-400/30 blur-3xl" />
        <div className="absolute -top-20 -left-20 h-72 w-72 rounded-full bg-blue-300/30 blur-3xl" />
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src={LOGO_URL} alt="BEAUTWELLZ" className="h-10 w-10 rounded-xl object-cover" />
            <p className="font-display font-extrabold text-lg">BEAUTWELLZ</p>
          </div>

          <h1 className="font-display text-2xl font-bold text-gray-900">Welcome back</h1>
          <p className="text-sm text-muted-foreground mb-6">Sign in to your BEAUTWELLZ workspace</p>

          <Tabs value={tab} onValueChange={(v) => { setTab(v); setError(""); }}>
            <TabsList className="grid grid-cols-2 w-full mb-5">
              <TabsTrigger value="login" data-testid="tab-login">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Sign Up</TabsTrigger>
            </TabsList>

            <form onSubmit={submit} className="space-y-4">
              <TabsContent value="register" className="m-0 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" data-testid="register-name" value={form.name} onChange={set("name")}
                    placeholder="Jane Doe" required={tab === "register"} />
                </div>
              </TabsContent>

              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" data-testid="login-email" value={form.email} onChange={set("email")}
                  placeholder="you@beautwellz.in" required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" data-testid="login-password" value={form.password} onChange={set("password")}
                  placeholder="••••••••" required />
              </div>

              {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}

              <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
                {loading ? "Please wait…" : tab === "login" ? "Sign In" : "Create account"}
              </Button>
            </form>
          </Tabs>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-border flex-1" />
            <span className="text-xs text-muted-foreground">or</span>
            <div className="h-px bg-border flex-1" />
          </div>

          <Button variant="outline" className="w-full gap-2" onClick={googleLogin} data-testid="google-login">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" />
            Continue with Google
          </Button>
        </div>
      </div>
    </div>
  );
}
